import * as React from "react";
import {ChangeEvent, useEffect} from "react";
import {sendConfigEditDataCall} from "../../BackendFacade/apiCalls";
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {HubPathRouting} from "../../../../HubFramework/pathBuilder";
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import {odxIconSets} from "../../../../OdxComponents/constants";
import OdxSelect from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Select";
import OdxInputField from "../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import {
    authLevels,
    ClientID,
    configDescriptionsTexts,
    defaultAuth,
    DisableTenantManagement,
    SignUpEventAPI,
    SignUpWithDefaultAuth,
    SignUpWithExceptionAuth,
    userTenant
} from "../../constants";
import OdxCheckbox from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Checkbox";
import OdxIcon from "../../../../OdxComponents/NativeOdx/Odx-Icon";
import {IAppConfig} from "../../Interfaces/IConfig";
import {IValueContainer} from "../../../../Utils/IValueContainer";
import {simplifyForHtmlId} from "../../../../Utils/transformer";
import OdxContentBox from "../../../../OdxComponents/NativeOdx/Odx-Contentbox";

interface Config_AddEditModalProps {
    closeFunction: () => void;
    configKeys: string[];
    selectedKey: string;
    clientId: string;
    configData: IAppConfig;
    refreshConfigData: () => void;
    authLevelChanges: string[];
    isEdit: boolean;
}

const Config_AddEditModal: React.FC<Config_AddEditModalProps> = ({
                                                                     closeFunction,
                                                                     configKeys,
                                                                     selectedKey,
                                                                     clientId,
                                                                     configData,
                                                                     refreshConfigData,
                                                                     authLevelChanges,
                                                                     isEdit
                                                                 }) => {
    const [currentKey, setCurrentKey] = React.useState<string>(selectedKey);
    const [currentValue, setCurrentValue] = React.useState<string>(
        configData[selectedKey] as string || ""
    );

    const [selectedValue, setSelectedValue] = React.useState<string>("");
    const [tmp, setTmp] = React.useState<string>("");
    const [selectedMail, setSelectedMail] = React.useState({
        mailReceivers: "",
        mailContent: "",
        subject: "",
        senderMail: "",
        templateID: "",
        templateData: "",
    });
    const baseId = `idms-config-modal-${simplifyForHtmlId(selectedKey || 'config')}-${isEdit ? 'edit' : 'add'}`;

    useEffect(() => {
        if (selectedKey != "") {
            switch (selectedKey) {
                case SignUpEventAPI:
                case SignUpWithDefaultAuth:
                case SignUpWithExceptionAuth:
                case isKeyInAuthLevelChanges():
                    setSelectedMail(JSON.parse(configData[selectedKey] as string))
                    break;

                default:
                    setSelectedValue(configData[selectedKey] as string)
                    break;
            }
        }
    }, []);

    const {setLoading, popupClose, createErrorToast} = useMain();

    const handleSave = () => {
        if (currentKey === "") {
            createErrorToast("Error saving value", "Error")
            return;
        }

        let sendValue: { [key: string]: string } = {};

        switch (currentKey) {
            case SignUpEventAPI:
            case SignUpWithDefaultAuth:
            case SignUpWithExceptionAuth:
            case isKeyInAuthLevelChanges():
                sendValue[currentKey] = JSON.stringify(selectedMail)
                break;

            default:
                sendValue[currentKey] = selectedValue
                break;
        }

        setLoading(true);

        // Construct the full updated configuration object
        const updatedConfig = {...configData, ...sendValue} as IAppConfig;

        // Send the updated config to the backend
        const valueObj: IValueContainer<IAppConfig> = {Value: updatedConfig};
        sendConfigEditDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, valueObj).then(async data => {
            if (!data.success) {
                createErrorToast(data.message, "Error");
            } else {
                popupClose();
                await refreshConfigData(); // Refresh UI data from the server
            }
            setLoading(false);
        });
        closeFunction();
    };

    const handleDelete = () => {
        if (!currentKey) {
            createErrorToast("Error deleting configuration", "Error");
            return;
        }

        setLoading(true);

        // Set the value to null for the specified key
        const updatedConfigData = {...configData, [currentKey]: null};

        // Send updated configuration to backend
        sendConfigEditDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id, {Value: updatedConfigData})
            .then(async data => {
                if (!data.success) {
                    createErrorToast(data.message, "Error");
                } else {
                    popupClose();
                    await refreshConfigData(); // Refresh the configuration list after deletion
                }
                setLoading(false);
            });
        closeFunction();
    };

    const authLevelsAdd = (value: string) => {
        if (value && !selectedValue.includes(value)) {
            setSelectedValue(prev => prev ? `${prev},${value}` : value);
            setTmp("");
        }
    };

    const authLevelsDelete = (item: string) => {
        setSelectedValue(prev => prev.split(",").filter(v => v !== item).join(","));
    };

    const isKeyInAuthLevelChanges = () => {
        if (authLevelChanges.indexOf(currentKey) !== -1 && currentKey.indexOf("AuthLevelChange") !== -1)
            return currentKey;
        return "false"
    }

    const getValueOptionFields = () => {
        const keyId = simplifyForHtmlId(currentKey || 'value');
        switch (currentKey) {
            case DisableTenantManagement:
                if (selectedValue === "") {
                    setSelectedValue("false");
                }
                return (
                    <OdxCheckbox
                        label="Activate to disable tenant management"
                        checked={selectedValue === "true"}
                        onChange={(event) => {
                            setSelectedValue(event.target.checked.toString());
                        }}
                        id={`${baseId}-${keyId}-toggle`}
                    />
                );

            case userTenant:
                return (
                    <OdxSelect
                        label="User Tenant"
                        value={selectedValue}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedValue(event.target.value)}
                        options={[
                            {value: "", label: "Please choose"},
                            {value: "draegerConnect", label: "draegerConnect (Dev)"},
                            {value: "draegerb2c", label: "draegerB2C (Prod)"},
                            {value: "oktaDev", label: "Okta Dev"},
                            {value: "oktaTest", label: "Okta Test"},
                            {value: "oktaProd", label: "Okta Prod"},
                        ]}
                        placeholder="Please choose"
                        id={`${baseId}-${keyId}-select`}
                    />
                );

            case authLevels:
                const authLevelsArray = selectedValue ? selectedValue.split(",") : [];

                return (
                    <>
                        <div className="odx-form-field">
                            <label className="odx-form-field__label">Value</label>
                            <div className="odx-form-field__inner">
                                {authLevelsArray.map((item, index) => (
                                    <odx-button
                                        key={index}
                                        variant="neutral"
                                        size="sm"
                                        type="button"
                                        onClick={() => authLevelsDelete(item)}
                                        className="mr-2 mb-2"
                                        id={`${baseId}-${keyId}-chip-${simplifyForHtmlId(item)}`}
                                    >
                                        {item}
                                        <odx-icon slot="suffix" name={`${odxIconSets.core}::trash`} size="md"></odx-icon>
                                    </odx-button>
                                ))}
                            </div>
                        </div>
                        <div className="odx-form-field">
                            <label className="odx-form-field__label">Set new Value</label>
                            <div className="odx-form-field__inner flex">
                                <OdxInputField
                                    id={`${baseId}-${keyId}-input`}
                                    placeholder="Value"
                                    value={tmp}
                                    onChange={(event) => setTmp(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            authLevelsAdd(tmp);
                                        }
                                    }}
                                    isRequired={false}
                                    label=""
                                    className="flex-grow mr-2"
                                />
                                <odx-button
                                    variant="primary"
                                    size="lg"
                                    type="button"
                                    onClick={() => authLevelsAdd(tmp)}
                                    id={`${baseId}-${keyId}-add-btn`}
                                >
                                    Add
                                </odx-button>
                            </div>
                        </div>
                    </>
                );

            case defaultAuth:
                if (configData.authLevels) {
                    const authLevelsOptions = configData.authLevels.split(",").map((item) => ({
                        value: item,
                        label: item,
                    }));
                    if (!selectedValue) {
                        setSelectedValue(authLevelsOptions[0].value);
                    }
                    return (
                        <OdxSelect
                            label="Default Auth Level"
                            value={selectedValue}
                            onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedValue(event.target.value)}
                            options={authLevelsOptions}
                            placeholder="Please choose"
                            id={`${baseId}-${keyId}-select`}
                        />
                    );
                } else {
                    return <p>There are no authLevels defined.</p>;
                }

            case ClientID:
                return (
                    <div className={"pt-11"}>
                        The client Id is used for the login. This ID does not exactly match the original B2C-ID, as
                        dashes and starting characters might be different. See the Documentation for more information.
                    </div>
                )

            case SignUpEventAPI:
            case SignUpWithDefaultAuth:
            case SignUpWithExceptionAuth:
            case isKeyInAuthLevelChanges():
                return (
                    <OdxContentBox title="Mail Configuration">
                        <OdxInputField
                            label="Mail Receivers"
                            id={`${baseId}-${keyId}-receivers`}
                            isRequired={false}
                            placeholder="Email"
                            value={selectedMail.mailReceivers}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, mailReceivers: event.target.value})
                            }
                        />
                        <OdxInputField
                            label="Mail Content"
                            id={`${baseId}-${keyId}-content`}
                            isRequired={false}
                            placeholder="Content"
                            value={selectedMail.mailContent}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, mailContent: event.target.value})
                            }
                            className="odx-input--textarea"
                        />
                        <OdxInputField
                            label="Subject"
                            id={`${baseId}-${keyId}-subject`}
                            isRequired={false}
                            placeholder="Subject"
                            value={selectedMail.subject}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, subject: event.target.value})
                            }
                        />
                        <OdxInputField
                            label="Sender Mail"
                            id={`${baseId}-${keyId}-sender`}
                            isRequired={false}
                            placeholder="Sender Email"
                            value={selectedMail.senderMail}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, senderMail: event.target.value})
                            }
                        />
                        <OdxInputField
                            label="Template ID"
                            id={`${baseId}-${keyId}-template-id`}
                            isRequired={false}
                            placeholder="Template ID"
                            value={selectedMail.templateID}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, templateID: event.target.value})
                            }
                        />
                        <OdxInputField
                            label="Template Data"
                            id={`${baseId}-${keyId}-template-data`}
                            isRequired={false}
                            placeholder="Template Data"
                            value={selectedMail.templateData}
                            onChange={(event) =>
                                setSelectedMail({...selectedMail, templateData: event.target.value})
                            }
                        />
                        <p>
                            You can use parameters by writing $(parameterName). Available parameters are:
                        </p>
                        <ul>
                            <li>userMail: Email of the affected user.</li>
                            <li>AppDisplayName: Display name of the app.</li>
                            <li>eventName: Name of the event that triggered this mail.</li>
                        </ul>
                        <p>
                            For example: Entering $(userMail) as mail receiver will send this mail to the user that
                            triggered the event.
                        </p>
                    </OdxContentBox>
                );

            default:
                return (
                    <OdxInputField
                        id={`${baseId}-${keyId}-value`}
                        placeholder="Value"
                        value={selectedValue}
                        onChange={(event) => setSelectedValue(event.target.value)}
                        isRequired={false}
                        label="Value"
                    />
                );
        }
    };

    return (
        <OdxModal
            size="small"
            title={isEdit ? "Edit Configuration" : "Add Configuration"}
            onClose={closeFunction}
            isOpen={true}
            footerButtons={[
                <odx-button
                    key="cancel"
                    id={`${baseId}-cancel-btn`}
                    variant="neutral"
                    size="lg"
                    type="button"
                    onClick={closeFunction}
                >
                    Cancel
                </odx-button>,
                isEdit && (
                    <odx-button
                        key="delete"
                        id={`${baseId}-delete-btn`}
                        variant="danger"
                        size="lg"
                        type="button"
                        onClick={handleDelete}
                    >
                        Delete
                    </odx-button>
                ),
                <odx-button
                    key="save"
                    id={`${baseId}-save-btn`}
                    variant="primary"
                    size="lg"
                    type="button"
                    onClick={handleSave}
                >
                    Save
                </odx-button>,
            ].filter(Boolean)}
            variant={"sidesheet"}
            id={baseId}
        >
            <OdxSelect
                onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    const newKey = event.target.value;
                    setCurrentKey(newKey);
                    setCurrentValue(configData[newKey] as string || "");
                }}
                value={currentKey}
                options={authLevelChanges.filter((e) => {
                    if (!isEdit) {
                        if (e == ClientID) {
                            return false;
                        }
                        return Object.keys(configData).indexOf(e) === -1;
                    }
                    return true;
                }).map((key) => ({
                    value: key,
                    label: key,
                }))}
                placeholder="Please choose"
                disabled={isEdit}
                label={"Key"}
                id={`${baseId}-key-select`}
            />
            {getValueOptionFields()}
            <div className={"pt-10"}>
                {configDescriptionsTexts[currentKey]}
            </div>
        </OdxModal>
    );

};

export default Config_AddEditModal;
